import {
  applyJsonNodeColdPatches,
  RTF_PATCH_GENESIS_VERSION,
} from './SiteDataMigrations';
import {weakMemo} from '@packages/utilities/memo';
import {enhanceExtraContext, type ExtraContext} from './ExtraContext';

import type {ReportType} from './types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

/**
 * RTF callers build one context and reuse it across every patched RTF field
 * (every field of every item in a read or backfill page), so enhance each
 * context object once instead of rebuilding `symbolNodeMap` over all of the
 * site's symbols per field. Keyed by object identity; RTF contexts are
 * write-once.
 */
const enhanceRTFExtraContext = weakMemo(enhanceExtraContext);

/**
 * Applies the shared `MIGRATIONS` array to a CMS Rich Text Field's `JSONNode[]`.
 *
 * RTF nodes ride the same `MIGRATIONS` list as page/component nodes — there is
 * no separate RTF patch array. They participate from a frozen genesis floor:
 * `RTF_PATCH_GENESIS_VERSION` is the default `fromVersion`, and the reducer
 * applies a patch when `fromVersion < version`, so only migrations authored
 * after the genesis floor (`version > RTF_PATCH_GENESIS_VERSION`) ever apply to
 * RTF content. Callers reading a `VersionedRichTextField` envelope pass the
 * stored `value.version` clamped up to the genesis floor instead (never
 * below it — see `RTF_PATCH_GENESIS_VERSION`'s doc comment), so an envelope
 * below `APP_VERSION` is migrated forward from wherever is later: where it
 * was authored, or the floor.
 *
 * Today `RTF_PATCH_GENESIS_VERSION === APP_VERSION`, so for bare arrays the
 * applicable slice is empty and `applyJsonNodeColdPatches` returns the input
 * reference unchanged — a ~0-cost no-op until the first post-genesis migration
 * ships. Callers are still responsible for cloning before calling (see the
 * read-path helpers): `applyPatch` mutates in place, and CMS RTF values can be
 * shared via DataLoader / Apollo cache references.
 *
 * Lives in its own module (rather than inside `SiteDataMigrations.ts`) so its
 * delegation to `applyJsonNodeColdPatches` crosses a module boundary and can be
 * asserted with `jest.spyOn`. An intra-module call compiles to a local binding
 * that the spy cannot intercept.
 *
 * @param jsonNodes    the RTF node array to migrate
 * @param fromVersion  the version the nodes were authored at; defaults to the
 *                     frozen genesis floor for bare arrays
 * @param logFn        reports per-node patch changes and warnings (idempotency
 *                     is each patch's own `needed()` responsibility)
 * @param extraContext the migration context threaded into every patch's
 *                     `context.extraContext`. RTF read paths supply the
 *                     bootstrapped `plugins` / `pluginComponents` here so
 *                     plugin-dependent migrations run on RTF content instead of
 *                     silently no-opping on an empty context. Omitted ⇒ the
 *                     empty context (the historical behavior). Enhanced once
 *                     per context object (see {@link enhanceRTFExtraContext}),
 *                     so callers must not mutate a context after passing it.
 * @return             the migrated node array (the same reference when no patch
 *                     applied)
 */
export const applyRTFJsonNodePatches = (
  jsonNodes: Array<JSONNode>,
  fromVersion: number = RTF_PATCH_GENESIS_VERSION,
  logFn: (arg1: ReportType) => void = () => {},
  extraContext?: ExtraContext
): Array<JSONNode> =>
  applyJsonNodeColdPatches(
    jsonNodes,
    fromVersion,
    logFn,
    extraContext ? enhanceRTFExtraContext(extraContext) : undefined
  );
